import React, { useState } from 'react';
import { Plus, Check, Shield, AlertTriangle, Zap, Trash2, Search, Sparkles, Bot, Globe, Cpu } from 'lucide-react';
import {
  Button, Pill, Card, TrustChip, Modal, Toast, Skeleton, EmptyState,
  Slideout, Table, FacetRail, TabNav, MenuDropdown,
  Sparkline, MetricCard, PostureBanner, PhaseGate, FiveLayerTrace
} from '../components/v2';

/**
 * V2PlaygroundPage — internal reference for the v2 design system.
 *
 * Mounted at /v2-playground when the feature flag
 * `features.rebirth.shell.v2` is on. SuperAdmin-typed access in App.jsx.
 *
 * Purpose:
 *   - Designers / PMs / engineers can see every v2 component variant
 *     in context without spinning up Storybook.
 *   - Acts as the visual contract for Phase 0 — a passing playground
 *     means the token system + Tailwind extension + components are
 *     wired correctly end-to-end.
 *
 * As more v2 components ship in Phase 0 (Slideout, Table, FacetRail,
 * MetricCard, PostureBanner, TrustChip, PhaseGate, FiveLayerTrace, etc.)
 * each gets a section here.
 */
function V2PlaygroundPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastTone, setToastTone] = useState('info');
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  const [slideoutTab, setSlideoutTab] = useState('summary');
  const [activeGovTab, setActiveGovTab] = useState('trust');
  const [facets, setFacets] = useState({
    archetype: new Set(['AutoLet', 'Let']),
    phase: new Set(['Act']),
    autonomy: new Set(['AUTONOMOUS', 'SUPERVISED']),
  });

  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets(prev => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };

  const isFacetChecked = (groupKey, optionKey) => !!facets[groupKey]?.has(optionKey);

  const showToast = (tone) => {
    setToastTone(tone);
    setToastOpen(true);
  };

  return (
    <div className="min-h-screen bg-v2-page text-v2-ink font-sans">
      {/* ── Header ── */}
      <header className="bg-v2-sidebar text-v2-ink-inverse px-7 py-5">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <div className="w-9 h-9 rounded-md bg-v2-brand text-white flex items-center justify-center font-extrabold">
            A
          </div>
          <div>
            <h1 className="text-[18px] font-bold tracking-tight">Agentlets v2 — Component Playground</h1>
            <p className="text-[11px] text-v2-ink-inverse-muted uppercase tracking-wider">
              Phase 0 · Foundation · gated by features.rebirth.shell.v2
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-7 py-8 space-y-10">

        {/* ═════════════════════ TOKENS ═════════════════════ */}
        <Section title="Color tokens" sub="The full v2 palette as it lands in Tailwind utilities + CSS variables.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SwatchTile name="Page" varName="--v2-bg-base" tw="bg-v2-page" border />
            <SwatchTile name="Surface" varName="--v2-bg-surface" tw="bg-v2-surface" border />
            <SwatchTile name="Muted" varName="--v2-bg-muted" tw="bg-v2-muted" />
            <SwatchTile name="Sidebar" varName="--v2-bg-sidebar" tw="bg-v2-sidebar" dark />

            <SwatchTile name="Action (teal)" varName="--v2-action" tw="bg-v2-action" dark />
            <SwatchTile name="Action soft" varName="--v2-action-soft" tw="bg-v2-action-soft" />
            <SwatchTile name="Brand (red)" varName="--v2-brand" tw="bg-v2-brand" dark />
            <SwatchTile name="Brand soft" varName="--v2-brand-soft" tw="bg-v2-brand-soft" />

            <SwatchTile name="Success" varName="--v2-success" tw="bg-v2-success" dark />
            <SwatchTile name="Warning" varName="--v2-warning" tw="bg-v2-warning" dark />
            <SwatchTile name="Danger" varName="--v2-danger" tw="bg-v2-danger" dark />
            <SwatchTile name="Trust (violet)" varName="--v2-accent-trust" tw="bg-v2-trust" dark />

            <SwatchTile name="Cyan accent" varName="--v2-accent-cyan" tw="bg-v2-cyan" dark />
            <SwatchTile name="Rose accent" varName="--v2-accent-rose" tw="bg-v2-rose" dark />
            <SwatchTile name="Border subtle" varName="--v2-border-subtle" tw="bg-v2-border-subtle" />
            <SwatchTile name="Border strong" varName="--v2-border-strong" tw="bg-v2-border-strong" />
          </div>
        </Section>

        {/* ═════════════════════ BUTTONS ═════════════════════ */}
        <Section title="Button" sub="Primary action color is teal (NOT brand red). Brand red is reserved for danger/critical only.">
          <Card>
            <Card.Header title="Variants" />
            <Card.Body>
              <Row>
                <Button variant="primary">Primary CTA</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="danger" iconLeft={<AlertTriangle />}>Engage Kill Switch</Button>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="Sizes" />
            <Card.Body>
              <Row>
                <Button size="sm">Small</Button>
                <Button size="md">Medium (default)</Button>
                <Button size="lg">Large</Button>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="With icons" />
            <Card.Body>
              <Row>
                <Button variant="primary" iconLeft={<Plus />}>New Agentlet</Button>
                <Button variant="secondary" iconRight={<Zap />}>Run now</Button>
                <Button variant="ghost" iconLeft={<Shield />}>Open protection</Button>
                <Button variant="danger" iconLeft={<Trash2 />}>Delete tenant</Button>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="States" />
            <Card.Body>
              <Row>
                <Button variant="primary" loading>Saving…</Button>
                <Button variant="secondary" loading>Refreshing</Button>
                <Button variant="primary" disabled>Disabled</Button>
                <Button variant="primary" fullWidth>Full width primary</Button>
              </Row>
            </Card.Body>
          </Card>
        </Section>

        {/* ═════════════════════ PILLS ═════════════════════ */}
        <Section title="Pill" sub="Status / categorical chip. Soft tints by default; solid reserved for active critical states.">
          <Card>
            <Card.Header title="Tones · soft variant (default)" />
            <Card.Body>
              <Row>
                <Pill tone="success" dot>Verified</Pill>
                <Pill tone="warning" dot>Supervised</Pill>
                <Pill tone="danger" dot>Blocked</Pill>
                <Pill tone="brand">CRITICAL</Pill>
                <Pill tone="action" dot>In review</Pill>
                <Pill tone="trust" dot>Autonomy 4 / 4</Pill>
                <Pill tone="cyan">PII</Pill>
                <Pill tone="rose">PHI</Pill>
                <Pill tone="muted">External</Pill>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="Tones · solid variant (use sparingly)" />
            <Card.Body>
              <Row>
                <Pill tone="success" variant="solid"><Check style={{ width: 12, height: 12 }} aria-hidden /> APPROVED</Pill>
                <Pill tone="danger" variant="solid">KILL ENGAGED</Pill>
                <Pill tone="brand" variant="solid">VIOLATION</Pill>
                <Pill tone="action" variant="solid">LIVE</Pill>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="Sizes" />
            <Card.Body>
              <Row>
                <Pill size="sm" tone="success">sm · success</Pill>
                <Pill size="md" tone="success">md · success</Pill>
              </Row>
            </Card.Body>
          </Card>
        </Section>

        {/* ═════════════════════ CARDS ═════════════════════ */}
        <Section title="Card" sub="Surface container. Hierarchy comes from border + shadow elevation, not surface tone.">
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <Card.Header title="Default elevation" />
              <Card.Body>
                <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
                  shadow-v2-sm + 1px subtle border. The canonical card. Used everywhere
                  unless a stronger elevation is required for emphasis.
                </p>
              </Card.Body>
            </Card>

            <Card elevation="md">
              <Card.Header title="Elevated" />
              <Card.Body>
                <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
                  shadow-v2-md. Reserved for hover state of interactive cards or
                  surfaces that genuinely need to lift off the page.
                </p>
              </Card.Body>
            </Card>

            <Card
              interactive
              onClick={() => alert('Interactive card clicked')}
              ariaLabel="Toxic combination — review"
            >
              <Card.Header title="Interactive" />
              <Card.Body>
                <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
                  Click or press Enter / Space. Lifts on hover, focus-visible ring,
                  role=button, tabIndex=0.
                </p>
              </Card.Body>
              <Card.Footer>
                <Pill tone="action">Press Enter</Pill>
              </Card.Footer>
            </Card>
          </div>
        </Section>

        {/* ═════════════════════ TRUST CHIP ═════════════════════ */}
        <Section title="TrustChip" sub="Composite trust score + autonomy tier indicator. Used everywhere an Agentlet appears.">
          <Card>
            <Card.Header title="By score band" />
            <Card.Body>
              <Row>
                <TrustChip score={95} />
                <TrustChip score={87} />
                <TrustChip score={77} />
                <TrustChip score={68} />
                <TrustChip score={45} />
              </Row>
            </Card.Body>
          </Card>
          <Card>
            <Card.Header title="Autonomy tier override (demoted agents)" />
            <Card.Body>
              <Row>
                <TrustChip score={92} autonomy="AUTONOMOUS" />
                <TrustChip score={92} autonomy="SUPERVISED" />
                <TrustChip score={92} autonomy="RESTRICTED" />
                <TrustChip score={92} autonomy="PROBATION" />
              </Row>
              <p className="text-[12px] text-v2-ink-muted mt-3">
                Same trust score (92), four autonomy levels &mdash; the dot count reflects the
                effective tier the platform has set, while the tone still reflects the raw score band.
              </p>
            </Card.Body>
          </Card>
          <Card>
            <Card.Header title="Sizes" />
            <Card.Body>
              <Row>
                <TrustChip score={87} size="sm" />
                <TrustChip score={87} size="md" />
              </Row>
            </Card.Body>
          </Card>
        </Section>

        {/* ═════════════════════ MODAL ═════════════════════ */}
        <Section title="Modal" sub="Sticky header + scrollable body + sticky footer. Escape and backdrop close (backdrop optional).">
          <Card>
            <Card.Body>
              <Row>
                <Button onClick={() => setModalOpen(true)}>Open default modal</Button>
                <Button variant="danger" onClick={() => setConfirmModalOpen(true)}>
                  Open confirm modal (no backdrop dismiss)
                </Button>
              </Row>
            </Card.Body>
          </Card>

          <Modal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Edit Agentlet"
            footer={
              <>
                <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                <Button variant="primary" onClick={() => setModalOpen(false)}>Save changes</Button>
              </>
            }
          >
            <p className="text-[13px] text-v2-ink-secondary leading-relaxed">
              This is a default <code className="font-mono-v2">md</code> modal. Press Escape, click
              the backdrop, click Cancel, or click the &times; to close. The body scrolls when content
              exceeds 90vh.
            </p>
            <p className="text-[13px] text-v2-ink-secondary leading-relaxed mt-3">
              Initial focus moves to the first focusable element on open; body scroll is locked
              while open and restored on close.
            </p>
          </Modal>

          <Modal
            isOpen={confirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            title="Engage Kill Switch?"
            size="sm"
            dismissOnBackdrop={false}
            footer={
              <>
                <Button variant="secondary" onClick={() => setConfirmModalOpen(false)}>Cancel</Button>
                <Button variant="danger" iconLeft={<AlertTriangle />} onClick={() => setConfirmModalOpen(false)}>
                  Engage Kill Switch
                </Button>
              </>
            }
          >
            <p className="text-[13px] text-v2-ink leading-relaxed">
              This will immediately pause every in-flight workflow for <strong>Aria Threat</strong>.
              Reactivation requires escalation to Platform Admin.
            </p>
            <p className="text-[12px] text-v2-ink-muted mt-3">
              Backdrop click is disabled to prevent accidental dismissal of irreversible actions.
            </p>
          </Modal>
        </Section>

        {/* ═════════════════════ TOAST ═════════════════════ */}
        <Section title="Toast" sub="Ephemeral notifications. role=alert + aria-live=polite. Auto-dismiss configurable.">
          <Card>
            <Card.Body>
              <Row>
                <Button variant="secondary" onClick={() => showToast('success')}>Success toast</Button>
                <Button variant="secondary" onClick={() => showToast('info')}>Info toast</Button>
                <Button variant="secondary" onClick={() => showToast('warning')}>Warning toast</Button>
                <Button variant="secondary" onClick={() => showToast('danger')}>Danger toast</Button>
              </Row>
            </Card.Body>
          </Card>
          {toastOpen && (
            <div className="fixed bottom-6 right-6 z-50">
              <Toast
                tone={toastTone}
                title={
                  toastTone === 'success' ? 'Skill approved' :
                  toastTone === 'warning' ? 'Trust score declining' :
                  toastTone === 'danger'  ? 'BLOCK enforcement triggered' :
                                            'Workflow updated'
                }
                description={
                  toastTone === 'success' ? 'grant_temp_access by m.chen@acme.io · SLA 4m 22s' :
                  toastTone === 'warning' ? 'Vela AppSec dropped from 84 to 81 in the last hour.' :
                  toastTone === 'danger'  ? 'Aria Threat · DPG layer 2 detected PHI in outbound payload.' :
                                            'Echo Compliance · 24 controls covered in batch B-2026-05-08-A.'
                }
                duration={5000}
                onDismiss={() => setToastOpen(false)}
              />
            </div>
          )}
        </Section>

        {/* ═════════════════════ SKELETON ═════════════════════ */}
        <Section title="Skeleton" sub="Loading placeholder. Skeletons match eventual layout — never spinners for lists / tables / charts.">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <Card.Header title="Text variants" />
              <Card.Body>
                <div className="space-y-3">
                  <div>
                    <Skeleton width="40%" />
                  </div>
                  <Skeleton lines={3} />
                </div>
              </Card.Body>
            </Card>
            <Card>
              <Card.Header title="Rect + circle" />
              <Card.Body>
                <div className="flex items-start gap-3">
                  <Skeleton variant="circle" width={40} />
                  <div className="flex-1 space-y-2">
                    <Skeleton width="60%" />
                    <Skeleton width="80%" />
                  </div>
                </div>
                <div className="mt-4">
                  <Skeleton variant="rect" height={120} />
                </div>
              </Card.Body>
            </Card>
          </div>
        </Section>

        {/* ═════════════════════ EMPTY STATE ═════════════════════ */}
        <Section title="EmptyState" sub="Empty list + first-run patterns. Includes the §3.15.4 First-5-Things checklist.">
          <div className="grid md:grid-cols-2 gap-4">
            <EmptyState
              icon={<Search size={20} />}
              title="No Agentlets match your filters"
              description="Try clearing facets or expanding the trust band to see more results."
              primaryAction={<Button variant="primary">Clear filters</Button>}
              secondaryAction={<Button variant="ghost">Learn about facets</Button>}
            />
            <EmptyState
              icon={<Sparkles size={20} />}
              title="Welcome to Acme Cybersec"
              description="Your tenant is empty. Walk through the first 5 steps to reach production."
              checklist={[
                { label: 'Connect your first integration', done: true, action: <Pill tone="success" size="sm">done</Pill> },
                { label: 'Create your first Agentlet', done: false, action: <Button size="sm" variant="ghost">Open</Button> },
                { label: 'Define a policy or import a framework', done: false },
                { label: 'Run an evaluation', done: false },
                { label: 'Publish to production', done: false },
              ]}
            />
          </div>
        </Section>

        {/* ═════════════════════ MENU DROPDOWN ═════════════════════ */}
        <Section title="MenuDropdown" sub='The "+ New X" archetype-aware launcher pattern (Design Principles §3.11.1).'>
          <Card>
            <Card.Body>
              <Row>
                <MenuDropdown
                  trigger={<Button variant="primary" iconLeft={<Plus />}>New Agentlet</Button>}
                  items={[
                    { id: 'hdr', header: 'Choose archetype' },
                    {
                      id: 'autolet',
                      label: 'Auto-create from policy',
                      description: 'AutoLet · Generated by wizard from policy docs',
                      iconTone: 'trust',
                      icon: <Sparkles size={14} />,
                      onSelect: () => alert('AutoLet flow'),
                    },
                    {
                      id: 'let',
                      label: 'Create native Agentlet',
                      description: 'Let · Hand-authored with B-D-A lifecycle',
                      iconTone: 'action',
                      icon: <Bot size={14} />,
                      onSelect: () => alert('Let flow'),
                    },
                    {
                      id: 'extelet',
                      label: 'Register external agent',
                      description: 'ExteLet · GEL-protected external + connector',
                      iconTone: 'cyan',
                      icon: <Globe size={14} />,
                      onSelect: () => alert('ExteLet flow'),
                    },
                    { id: 'div', divider: true },
                    {
                      id: 'syslet',
                      label: 'Provision system agent',
                      description: 'SysLet · Super Admin only',
                      iconTone: 'muted',
                      icon: <Cpu size={14} />,
                      disabled: true,
                    },
                    { id: 'ftr', footer: 'Role-gated · see §3.11.1' },
                  ]}
                />
                <MenuDropdown
                  align="left"
                  trigger={<Button variant="secondary">Saved views</Button>}
                  items={[
                    { id: 'mine', label: "My team's risky agents", description: '12 results' },
                    { id: 'pending', label: 'Pending approvals this week', description: '7 results' },
                    { id: 'recent', label: 'Recently changed autonomy', description: '3 results' },
                  ]}
                />
              </Row>
            </Card.Body>
          </Card>
        </Section>

        {/* ═════════════════════ SLIDEOUT ═════════════════════ */}
        <Section title="Slideout" sub="Universal right-edge detail panel. Triage-then-author pattern from §3.13.">
          <Card>
            <Card.Body>
              <Button onClick={() => { setSlideoutOpen(true); setSlideoutTab('summary'); }}>
                Open Agentlet detail slideout
              </Button>
            </Card.Body>
          </Card>
          <Slideout
            isOpen={slideoutOpen}
            onClose={() => setSlideoutOpen(false)}
            ariaLabel="Aria Threat detail"
            tabs={[
              { id: 'summary', label: 'Summary' },
              { id: 'audit', label: 'Audit' },
              { id: 'verdict', label: 'Five-Layer Verdict' },
              { id: 'gates', label: 'Phase Gates' },
            ]}
            activeTabId={slideoutTab}
            onTabChange={setSlideoutTab}
            header={
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Pill tone="muted" size="sm">External</Pill>
                  <Pill tone="cyan" size="sm">Threat detection</Pill>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-v2-md bg-v2-brand-soft text-v2-brand-strong flex items-center justify-center font-bold text-[14px]">
                    AT
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-[18px] font-bold tracking-tight m-0">Aria Threat</h2>
                    <p className="text-[12px] text-v2-ink-secondary">External · last action 14:01</p>
                  </div>
                  <TrustChip score={76} autonomy="SUPERVISED" />
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Button variant="primary" size="sm">Open detail page</Button>
                  <Button variant="secondary" size="sm">Run eval</Button>
                  <Button variant="danger" size="sm" iconLeft={<AlertTriangle />}>Kill switch</Button>
                </div>
              </div>
            }
          >
            {slideoutTab === 'summary' && (
              <div className="space-y-4 text-[13px] text-v2-ink-secondary leading-relaxed">
                <p>
                  Sample slideout body for the <strong>Summary</strong> tab. In production this
                  would render the AgentTrustScorePanel, recent executions, connectors, and skills
                  using composed v2 primitives.
                </p>
                <Card padding="sm">
                  <Card.Header title="Last 24h" />
                  <Card.Body>
                    <div className="grid grid-cols-2 gap-3 text-[12px]">
                      <div><div className="text-v2-ink-muted text-[10.5px] uppercase">Executions</div><div className="text-v2-ink font-semibold text-[18px]">2,418</div></div>
                      <div><div className="text-v2-ink-muted text-[10.5px] uppercase">Approvals</div><div className="text-v2-ink font-semibold text-[18px]">42</div></div>
                      <div><div className="text-v2-ink-muted text-[10.5px] uppercase">Blocks</div><div className="text-v2-brand-strong font-semibold text-[18px]">1</div></div>
                      <div><div className="text-v2-ink-muted text-[10.5px] uppercase">p95 latency</div><div className="text-v2-ink font-semibold text-[18px]">820ms</div></div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}
            {slideoutTab === 'audit' && (
              <div className="text-[13px] text-v2-ink-secondary">Hash-chained audit timeline goes here.</div>
            )}
            {slideoutTab === 'verdict' && (
              <div className="text-[13px] text-v2-ink-secondary">Five-Layer Verdict trace goes here (PR #4).</div>
            )}
            {slideoutTab === 'gates' && (
              <div className="text-[13px] text-v2-ink-secondary">Phase Gate criteria go here (PR #4).</div>
            )}
          </Slideout>
        </Section>

        {/* ═════════════════════ TABNAV ═════════════════════ */}
        <Section title="TabNav" sub="Tab bar with arrow-key navigation. Used on every tabbed surface.">
          <Card padding="none">
            <TabNav
              tabs={[
                { id: 'trust', label: 'Trust' },
                { id: 'compliance', label: 'Compliance' },
                { id: 'dataflow', label: 'Data Flow', dot: 'warning', badge: <Pill tone="warning" size="sm">3</Pill> },
                { id: 'audit', label: 'Audit Trail' },
                { id: 'wi', label: 'Workflow Intelligence' },
              ]}
              activeId={activeGovTab}
              onChange={setActiveGovTab}
              ariaLabel="Governance Center sub-views"
            />
            <div className="p-5 text-[13px] text-v2-ink-secondary">
              Active tab: <strong className="text-v2-ink">{activeGovTab}</strong>. Use ArrowLeft / ArrowRight / Home / End on focus.
            </div>
          </Card>
        </Section>

        {/* ═════════════════════ FACET RAIL + TABLE ═════════════════════ */}
        <Section title="FacetRail + Table" sub="The composition that powers every inventory page.">
          <div className="grid gap-5" style={{ gridTemplateColumns: '220px 1fr' }}>
            <FacetRail
              ariaLabel="Agentlet filters"
              groups={[
                {
                  key: 'archetype',
                  label: 'Archetype',
                  options: [
                    { key: 'AutoLet', label: 'AutoLet', count: 8, checked: isFacetChecked('archetype', 'AutoLet') },
                    { key: 'Let', label: 'Let', count: 27, checked: isFacetChecked('archetype', 'Let') },
                    { key: 'ExteLet', label: 'ExteLet', count: 14, checked: isFacetChecked('archetype', 'ExteLet') },
                    { key: 'SysLet', label: 'SysLet', count: 3, checked: isFacetChecked('archetype', 'SysLet') },
                  ],
                },
                {
                  key: 'phase',
                  label: 'Phase',
                  options: [
                    { key: 'Build', label: 'Build', count: 4, checked: isFacetChecked('phase', 'Build') },
                    { key: 'Decide', label: 'Decide', count: 3, checked: isFacetChecked('phase', 'Decide') },
                    { key: 'Act', label: 'Act', count: 35, checked: isFacetChecked('phase', 'Act') },
                  ],
                },
                {
                  key: 'autonomy',
                  label: 'Autonomy',
                  options: [
                    { key: 'AUTONOMOUS', label: 'Autonomous', count: 27, checked: isFacetChecked('autonomy', 'AUTONOMOUS') },
                    { key: 'SUPERVISED', label: 'Supervised', count: 10, checked: isFacetChecked('autonomy', 'SUPERVISED') },
                    { key: 'RESTRICTED', label: 'Restricted', count: 4, checked: isFacetChecked('autonomy', 'RESTRICTED') },
                    { key: 'PROBATION', label: 'Probation', count: 1, checked: isFacetChecked('autonomy', 'PROBATION') },
                  ],
                },
              ]}
              onToggle={toggleFacet}
            />
            <Table
              ariaLabel="Agentlet inventory"
              columns={[
                { key: 'name', header: 'Name', sortable: true, render: (r) => (
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-v2-sm flex items-center justify-center font-bold text-[10px] ${r.iconTone}`}>{r.iconLabel}</div>
                    <div>
                      <div className="font-semibold text-v2-ink">{r.name}</div>
                      <div className="text-[10.5px] text-v2-ink-muted">{r.category}</div>
                    </div>
                  </div>
                )},
                { key: 'type', header: 'Type', render: (r) => <Pill tone={r.type === 'External' ? 'trust' : 'cyan'} size="sm">{r.type}</Pill> },
                { key: 'phase', header: 'Phase', render: (r) => <Pill tone="cyan" size="sm">{r.phase}</Pill> },
                { key: 'trust', header: 'Trust', render: (r) => <TrustChip size="sm" score={r.trust} autonomy={r.autonomy} /> },
                { key: 'last', header: 'Last action', align: 'right', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.last}</span> },
              ]}
              rows={[
                { id: 'sophie', name: 'Sophie Cognito', category: 'IAM · owner: IAM', iconLabel: 'SC', iconTone: 'bg-v2-cyan-soft text-v2-cyan', type: 'Native', phase: 'Act', trust: 94, autonomy: 'AUTONOMOUS', last: '14:08' },
                { id: 'aria', name: 'Aria Threat', category: 'Threat · owner: SOC', iconLabel: 'AT', iconTone: 'bg-v2-brand-soft text-v2-brand-strong', type: 'External', phase: 'Act', trust: 76, autonomy: 'SUPERVISED', last: '14:01' },
                { id: 'echo', name: 'Echo Compliance', category: 'Compliance · owner: GRC', iconLabel: 'EC', iconTone: 'bg-v2-success-soft text-v2-success', type: 'Native', phase: 'Act', trust: 91, autonomy: 'AUTONOMOUS', last: '13:52' },
                { id: 'vela', name: 'Vela AppSec', category: 'AppSec · owner: AppSec', iconLabel: 'VA', iconTone: 'bg-v2-warning-soft text-v2-warning', type: 'Native', phase: 'Act', trust: 81, autonomy: 'SUPERVISED', last: '13:44' },
              ]}
              rowKey={(r) => r.id}
              onRowClick={(r) => alert(`Clicked: ${r.name}`)}
              density="comfortable"
            />
          </div>
        </Section>

        {/* ═════════════════════ POSTURE BANNER ═════════════════════ */}
        <Section title="PostureBanner" sub="Top-of-page narrative banner. The trust-narrative moment from §0.4.">
          <PostureBanner
            score={87}
            title="Fleet posture: Strong — 38 of 42 Agentlets protected"
            message={
              <>
                Trust score average <strong className="text-v2-ink">87.3</strong>, up 2.1 vs last week.{' '}
                <strong className="text-v2-brand">3 toxic combinations</strong> require review.
              </>
            }
            action={<Button variant="primary">Review priorities</Button>}
          />
        </Section>

        {/* ═════════════════════ METRIC CARDS ═════════════════════ */}
        <Section title="MetricCard + Sparkline" sub="Hero outcomes for the Overview page. Composes Sparkline (no chart library — just SVG).">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              label="Hours saved"
              value="12,847"
              unit="hrs"
              tone="success"
              icon={<span style={{fontSize: 11, fontWeight: 800}}>H</span>}
              delta={{ value: '18.4%', direction: 'up', baseline: 'last 30 days', intent: 'positive' }}
              trend={[22,20,18,15,16,12,14,9,8,10,6,5,3,2]}
            />
            <MetricCard
              label="Realized ROI"
              value="$3.24"
              unit="M"
              tone="trust"
              icon={<span style={{fontSize: 11, fontWeight: 800}}>$</span>}
              delta={{ value: '11-mo payback', direction: 'up', baseline: '387% 3-yr', intent: 'positive' }}
              trend={[26,25,22,21,18,16,12,11,8,6,4]}
            />
            <MetricCard
              label="Throughput / wk"
              value="184,920"
              tone="cyan"
              icon={<span style={{fontSize: 11, fontWeight: 800}}>⚡</span>}
              delta={{ value: '98.7% success', direction: 'up', baseline: 'p95 940ms', intent: 'positive' }}
              trend={[18,14,20,12,16,10,13,8,11,6,9,4]}
              trendVariant="bar"
            />
            <MetricCard
              label="Risk avoided"
              value="1,243"
              tone="brand"
              icon={<span style={{fontSize: 11, fontWeight: 800}}>!</span>}
              delta={{ value: 'interventions', direction: 'up', baseline: 'MTTR −71%', intent: 'positive' }}
              trend={[18,16,17,12,15,10,11,7,9,5,4]}
            />
          </div>
        </Section>

        {/* ═════════════════════ PHASE GATE ═════════════════════ */}
        <Section title="PhaseGate" sub="Build → Decide → Act gate validation card with explicit criteria + remediation.">
          <PhaseGate
            phase="ACT"
            criteria={[
              { id: '1', passed: true,  label: '≥1 user or group assigned', detail: '3 assigned' },
              { id: '2', passed: true,  label: 'Valid gateway URL configured' },
              { id: '3', passed: true,  label: 'Agent status: active' },
              { id: '4', passed: true,  label: 'All non-blocked skills published' },
              { id: '5', passed: false, label: 'No active kill switch', detail: '1 active on quarantine_endpoint',
                remediation: { label: 'Resolve', onClick: () => alert('Kill switch panel') } },
              { id: '6', passed: true,  label: 'AgentLifecycleWorkflow reports healthy' },
            ]}
            advanceAction={<Button variant="primary" size="sm" disabled>Publish (1 gate failing)</Button>}
          />
        </Section>

        {/* ═════════════════════ FIVE LAYER TRACE ═════════════════════ */}
        <Section title="FiveLayerTrace" sub="The platform's biggest differentiator visualized. Click any layer to drill in.">
          <Card>
            <Card.Header title="Last execution · Aria Threat · investigate_session" />
            <Card.Body>
              <FiveLayerTrace
                onLayerClick={(l) => alert(`Drilling into Layer ${l.layer}: ${l.verdict}`)}
                layers={[
                  { layer: 1, verdict: 'allow' },
                  { layer: 2, verdict: 'block' },
                  { layer: 3, verdict: 'skipped' },
                  { layer: 4, verdict: 'skipped' },
                  { layer: 5, verdict: 'logged' },
                ]}
              />
              <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed mt-4 m-0">
                Layer 2 DPG classifier detected MRN-shaped fields in outbound payload to{' '}
                <code className="font-mono-v2 text-v2-ink">crowdstrike.alerts</code>. Policy{' '}
                <code className="font-mono-v2 text-v2-ink">phi.outbound.deny</code> matched.
                Execution rejected, hash-chained event written.
              </p>
            </Card.Body>
          </Card>
        </Section>

        {/* ═════════════════════ COMPOSITION DEMO ═════════════════════ */}
        <Section title="Composition · live posture banner sketch" sub="A small slice of the redesigned Overview page assembled from v2 primitives.">
          <Card padding="lg">
            <div className="flex items-center gap-5">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'conic-gradient(var(--v2-success) 0% 87%, var(--v2-border-default) 87% 100%)' }}
              >
                <div className="w-[54px] h-[54px] rounded-full bg-v2-surface flex flex-col items-center justify-center">
                  <span className="text-[18px] font-extrabold leading-none">87</span>
                  <span className="text-[8.5px] text-v2-ink-muted uppercase tracking-wider mt-0.5">Posture</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-[16px] font-bold mb-1">Fleet posture: Strong &mdash; 38 of 42 Agentlets protected</h3>
                <p className="text-[12.5px] text-v2-ink-secondary">
                  Trust score average <span className="font-semibold text-v2-ink">87.3</span>, up 2.1 vs last week. <span className="text-v2-brand font-semibold">3 toxic combinations</span> require review.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Pill tone="success" dot size="sm">Hash chain intact</Pill>
                  <Pill tone="action" size="sm">184,920 protected exec / wk</Pill>
                  <Pill tone="trust" size="sm">7 approvals pending</Pill>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button variant="primary">Review priorities</Button>
                <Button variant="secondary">Export Snapshot</Button>
              </div>
            </div>
          </Card>
        </Section>

        <footer className="text-center text-[11px] text-v2-ink-muted pt-4 pb-8 border-t border-v2-border-subtle">
          Phase 0 · Components: Button, Pill, Card · Tokens loaded from
          <code className="font-mono-v2 mx-1 text-v2-ink">src/styles/v2-tokens.css</code>
          · Tailwind extended via <code className="font-mono-v2 mx-1 text-v2-ink">tailwind.config.js</code>.
        </footer>
      </main>
    </div>
  );
}

/* ── Local helpers (playground-only chrome) ───────────────────────── */
function Section({ title, sub, children }) {
  return (
    <section className="space-y-3">
      <header>
        <h2 className="text-[18px] font-bold tracking-tight m-0">{title}</h2>
        {sub && <p className="text-[12.5px] text-v2-ink-secondary mt-1">{sub}</p>}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Row({ children }) {
  return <div className="flex flex-wrap items-center gap-3">{children}</div>;
}

function SwatchTile({ name, varName, tw, dark = false, border = false }) {
  return (
    <div
      className={`rounded-v2-md p-3 ${tw} ${border ? 'border border-v2-border-subtle' : ''}`}
    >
      <div className={`text-[11.5px] font-semibold ${dark ? 'text-white' : 'text-v2-ink'}`}>
        {name}
      </div>
      <div className={`font-mono-v2 text-[10.5px] mt-0.5 ${dark ? 'text-white/70' : 'text-v2-ink-muted'}`}>
        {varName}
      </div>
    </div>
  );
}

export default V2PlaygroundPage;
